import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { IAttachedFile } from '../services/general-agent.service';
import { IThread, IThreadMetadata, ITokenLimitsAgent, LLM_PROVIDERS, MessageItem } from '../models/model';
import { ChatMessagePayload, IMessageFeedback, MESSAGE_FEEDBACK, PLAN_FEEDBACK } from '../models/types';

export interface IThreadListItem {
    _id: string;
    title?: string;
    threadMetadataId?: string;
    createdAt: Date | string;
    updatedAt?: Date | string;
}

export interface IRunThreadResponse {
    success: boolean;
    title?: string;
    data?: {
        thread?: Pick<IThread, '_id'>;
        threadMetadata?: Array<Pick<IThreadMetadata, '_id'>>;
        threadId?: string;
        threadMetadataId?: string;
    };
}

export interface IThreadAgentService {
    getThreads(primaryId: string): Promise<{ success?: boolean; data?: IThreadListItem[] }>;
    runThread(data: ChatMessagePayload & { threadId: string; threadMetadataId: string }): Promise<IRunThreadResponse>;
    pauseThread(threadId: string, threadMetadataId: string): Promise<{ success: boolean }>;
    resumeThread(
        threadId: string,
        planFeedback?: PLAN_FEEDBACK,
        toolParams?: any[],
        selectedProvider?: LLM_PROVIDERS,
        editedPlan?: Record<string, any>,
        revisionFeedback?: string,
        /** Decision made on a PROMPT_REUSE_SUGGESTION interrupt. */
        reuseDecision?: { action: 'use_existing' | 'generate_new' | 'reject'; selected_prompt_id?: string },
    ): Promise<any>;
    getThread(threadId: string): Promise<{
        success: boolean;
        data: {
            thread: IThread;
            threadMetadata: IThreadMetadata[];
            conversation: { messages: MessageItem[] };
            token_limits_agent: ITokenLimitsAgent | null;
            messageFeedbacks: IMessageFeedback[];
            pendingFiles?: IAttachedFile[];
            // Pending PromptEnhancerMiddleware interrupt — present when the
            // thread is paused awaiting user approval of an enhanced prompt.
            // Used to restore the enhancement card on page reload.
            pendingEnhancement?: { markdown: string } | null;
            // Pending reuse-suggestion interrupt — present when the thread is
            // paused at a PROMPT_REUSE_SUGGESTION interrupt. Used to restore
            // the reuse card on page reload.
            pendingReuseCandidates?: any[] | null;
            isOwner?: boolean;
            permissions?: { canView: boolean; canEdit: boolean; canComment: boolean; restricted: boolean };
        };
    }>;
    updateThreadTitle(threadId: string, title: string): Promise<{ success: boolean; data: IThread }>;
    deleteThread(threadId: string): Promise<{ success: boolean }>;
    submitFeedback?(data: {
        threadId: string;
        messageId: string;
        feedback: MESSAGE_FEEDBACK;
        details: string;
        threadMetadataId: string;
    }): Observable<{ success: boolean; data?: IMessageFeedback }>;
    downloadCanvas?(attachmentId: string, versionId: string): Promise<{ success: boolean; data?: { data: number[] } }> | null;
}

export const THREAD_AGENT_SERVICE = new InjectionToken<IThreadAgentService>('THREAD_AGENT_SERVICE');
